import React, { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'

import { CarbonFootprintImpactValue } from './CarbonFootprintImpactUnit'
import Icon from '../../atoms/icons/icon/Icon'
import { Button } from '../../../shadcn/components/ui/button'
import { Card } from '../../../shadcn/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '../../../shadcn/components/ui/collapsible'

export type CarbonFootprintDetailsProps = {
  totalEmission: number
  housingEmission: number
  restorationEmission: number
  transportEmission: number
  activitiesEmission: number
  onModify?: (e: React.MouseEvent) => void
}

const CarbonFootprintDetails = ({
  totalEmission,
  housingEmission,
  restorationEmission,
  transportEmission,
  activitiesEmission,
  onModify,
}: CarbonFootprintDetailsProps) => {
  const [isOpen, setIsOpen] = React.useState(true)

  return (
    <Card
      className='cursor-pointer px-4 py-5'
      onClick={() => setIsOpen(!isOpen)}
    >
      <Collapsible open={isOpen}>
        <CollapsibleTrigger className='flex w-full items-center justify-between'>
          <div className='flex items-center gap-1'>
            <Icon name='leaf' size={20} className='text-green-500' />
            <span className='hidden font-[500] md:inline-block'>
              <FormattedMessage
                id='ui.CarbonFootprintDetails.triggerLabel'
                defaultMessage='L’empreinte carbone de votre événement : '
              />
            </span>
            <span className='font-[500] md:hidden'>
              <FormattedMessage
                id='ui.CarbonFootprintDetails.triggerLabelMobile'
                defaultMessage='Emissions : '
              />
            </span>
            <CarbonFootprintImpactValue value={totalEmission} />
          </div>
          <div className='flex items-center gap-4'>
            {onModify && (
              <Button
                className='h-[24px] border-black bg-white text-black'
                onClick={onModify}
              >
                <FormattedMessage
                  id='ui.CarbonFootprintDetails.buttonModify'
                  defaultMessage='Modifier'
                />
              </Button>
            )}
            <Icon name={isOpen ? 'arrow-up' : 'arrow-down'} size={16} />
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className='mt-4'>
          <CarbonFootprintRows
            {...{
              housingEmission,
              restorationEmission,
              transportEmission,
              activitiesEmission,
            }}
          />
          <div className='mt-3 text-left text-sm text-grey-700'>
            <FormattedMessage
              id='ui.CarbonFootprintDetails.content'
              defaultMessage='Une estimation carbone repose sur une méthodologie rigoureuse et détaillée visant à évaluer l’impact environnemental d’une activité, d’une entreprise ou d’un projet. Ce processus comprend plusieurs étapes essentielles permettant d’identifier et de quantifier les émissions de gaz à effet de serre générées par les différentes opérations et sources d’énergie utilisées. '
            />
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}

export default CarbonFootprintDetails

const CarbonFootprintRow = ({
  content,
  value,
}: {
  content: ReactNode
  value: number
}) => {
  return (
    <div className='flex items-center justify-between rounded-[4px] border border-grey-200 p-3 text-[14px]'>
      <span className='font-[500]'>{content}</span>
      <CarbonFootprintImpactValue value={value} />
    </div>
  )
}

const CarbonFootprintRows = ({
  housingEmission,
  restorationEmission,
  transportEmission,
  activitiesEmission,
}: {
  housingEmission: number
  restorationEmission: number
  transportEmission: number
  activitiesEmission: number
}) => {
  return (
    <div className='flex flex-col gap-2'>
      <CarbonFootprintRow
        content={
          <FormattedMessage
            id='ui.CarbonFootprintDetails.housingScore'
            defaultMessage='Hébergement'
          />
        }
        value={housingEmission}
      />
      <CarbonFootprintRow
        content={
          <FormattedMessage
            id='ui.CarbonFootprintDetails.restorationScore'
            defaultMessage='Restauration'
          />
        }
        value={restorationEmission}
      />
      <CarbonFootprintRow
        content={
          <FormattedMessage
            id='ui.CarbonFootprintDetails.transportScore'
            defaultMessage='Transport'
          />
        }
        value={transportEmission}
      />
      <CarbonFootprintRow
        content={
          <FormattedMessage
            id='ui.CarbonFootprintDetails.activityScore'
            defaultMessage='Activité'
          />
        }
        value={activitiesEmission}
      />
    </div>
  )
}
